import React, { useState } from 'react'
import { Calendar, Info, ChevronDown, ChevronUp } from 'lucide-react'

/**
 * DataFreshnessHeader - compact, collapsible data freshness display.
 */

const formatOperatorFreshness = (operatorCode, freshnessData, thresholds) => {
  if (!freshnessData?.dashboard) {
    return null
  }

  const { last_update, source_type, comment } = freshnessData.dashboard
  const greenHours = thresholds?.green_hours || 24
  const yellowHours = thresholds?.yellow_hours || 48

  const operatorNames = {
    'ORANGE': 'Orange',
    'TMOBILE': 'T-Mobile',
    'PLUS': 'Plus',
    'P4': 'Play'
  }

  const displayName = operatorNames[operatorCode] || operatorCode

  if (source_type === 'live') {
    return { name: displayName, dateText: 'Live', icon: '🟢', hasData: true }
  }

  if (!last_update) {
    return { name: displayName, dateText: null, icon: '⚪', hasData: false }
  }

  const updateDate = new Date(last_update)
  const now = new Date()
  const hoursDiff = (now - updateDate) / (1000 * 60 * 60)

  const dateText = updateDate.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })

  let icon
  if (hoursDiff < greenHours) {
    icon = '🟢'
  } else if (hoursDiff < yellowHours) {
    icon = '🟡'
  } else {
    icon = '🔴'
  }

  return { name: displayName, dateText, icon, hasData: true }
}

const DataFreshnessHeader = ({ dataFreshness, thresholds, operatorOrder }) => {
  const [expanded, setExpanded] = useState(false)

  if (!dataFreshness || Object.keys(dataFreshness).length === 0) {
    return null
  }

  const displayOrder = operatorOrder && operatorOrder.length > 0
    ? operatorOrder
    : ['TMOBILE', 'ORANGE', 'PLUS', 'P4']

  const operatorItems = displayOrder
    .filter(code => dataFreshness[code])
    .map(code => formatOperatorFreshness(code, dataFreshness[code], thresholds))
    .filter(Boolean)

  if (operatorItems.length === 0) {
    return null
  }

  return (
    <div className="bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 text-blue-500" />
          <span className="text-xs font-medium text-blue-700">Aktualność danych</span>
          {!expanded && (
            <span className="text-xs text-gray-500 ml-1">
              {operatorItems.map(i => `${i.icon} ${i.name}: ${i.dateText || '—'}`).join('  ·  ')}
            </span>
          )}
        </div>
        {expanded
          ? <ChevronUp className="h-3.5 w-3.5 text-blue-400" />
          : <ChevronDown className="h-3.5 w-3.5 text-blue-400" />
        }
      </button>

      {expanded && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
          {operatorItems.map((item) => (
            <div key={item.name} className="flex items-center gap-1.5 text-xs text-gray-700">
              <span>{item.icon}</span>
              <span className="font-medium">{item.name}:</span>
              <span>{item.dateText || <span className="text-gray-400 italic">brak</span>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DataFreshnessHeader
